// src/components/risk/ClientRiskQueue.jsx
// Sortable table of at-risk clients with quick action buttons

import React, { useState, useMemo, useEffect } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Phone,
  MessageSquare,
  Mail,
  Clock,
  AlertTriangle,
  Search,
  Filter,
  ArrowUpDown
} from 'lucide-react';
import { getRiskCategory, RISK_CATEGORIES } from './constants';
import { RiskScoreBadge } from './RiskScoreGauge';

const ClientRiskQueue = ({
  clients = [],
  totalClients = null, // Pass actual total from server
  onClientClick,
  onQuickSMS,
  onQuickCall,
  onQuickEmail,
  selectedClientId = null,
  filterCategory = null,
  className = ''
}) => {
  const [sortField, setSortField] = useState('risk_score');
  const [sortDir, setSortDir] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(filterCategory);

  // Sync filterCategory prop with local state when it changes
  useEffect(() => {
    setCategoryFilter(filterCategory);
  }, [filterCategory]);

  // Filter and sort clients
  const filteredClients = useMemo(() => {
    let filtered = [...clients];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(term) ||
        (c.phone || '').includes(term) ||
        (c.email || '').toLowerCase().includes(term)
      );
    }

    // Category filter
    if (categoryFilter) {
      filtered = filtered.filter(c => {
        const cat = getRiskCategory(c.risk_score);
        return cat.key === categoryFilter;
      });
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle string comparisons
      if (sortField === 'name') {
        aVal = `${a.first_name || ''} ${a.last_name || ''}`.toLowerCase();
        bVal = `${b.first_name || ''} ${b.last_name || ''}`.toLowerCase();
      }

      // Handle date comparisons
      if (sortField === 'last_contact') {
        aVal = a.last_contact_date ? new Date(a.last_contact_date).getTime() : 0;
        bVal = b.last_contact_date ? new Date(b.last_contact_date).getTime() : 0;
      }

      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [clients, searchTerm, categoryFilter, sortField, sortDir]);

  // Toggle sort
  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Format relative time
  const formatLastContact = (date) => {
    if (!date) return 'Never';
    const d = new Date(date);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const SortHeader = ({ field, children }) => (
    <button
      onClick={() => toggleSort(field)}
      className="flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
    >
      {children}
      {sortField === field ? (
        sortDir === 'desc' ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-50" />
      )}
    </button>
  );

  return (
    <div className={`bg-slate-800/50 rounded-xl border border-slate-700 ${className}`}>
      {/* Header with search and filter */}
      <div className="p-4 border-b border-slate-700">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50"
            />
          </div>

          {/* Category filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <select
              value={categoryFilter || ''}
              onChange={(e) => setCategoryFilter(e.target.value || null)}
              className="pl-9 pr-8 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-amber-500/50 appearance-none cursor-pointer"
            >
              <option value="">All Categories</option>
              {RISK_CATEGORIES.map(cat => (
                <option key={cat.key} value={cat.key}>{cat.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-2 text-sm text-slate-400">
          Showing {filteredClients.length} of {totalClients || clients.length} clients
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider border-b border-slate-700">
              <th className="px-4 py-3">
                <SortHeader field="name">Client</SortHeader>
              </th>
              <th className="px-4 py-3">
                <SortHeader field="risk_score">Score</SortHeader>
              </th>
              <th className="px-4 py-3 hidden sm:table-cell">
                <SortHeader field="last_contact">Last Contact</SortHeader>
              </th>
              <th className="px-4 py-3 hidden md:table-cell">Action Needed</th>
              <th className="px-4 py-3 text-right">Quick Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {filteredClients.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  No clients match your criteria
                </td>
              </tr>
            ) : (
              filteredClients.map((client) => {
                const category = getRiskCategory(client.risk_score);
                const isSelected = selectedClientId === client.id;

                return (
                  <tr
                    key={client.id}
                    onClick={() => onClientClick?.(client)}
                    className={`hover:bg-slate-700/30 cursor-pointer transition-colors ${
                      isSelected ? 'bg-amber-500/10 ring-1 ring-inset ring-amber-500/30' : ''
                    }`}
                  >
                    {/* Client name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                          style={{ backgroundColor: category.color }}
                        >
                          {(client.first_name || '?')[0]}{(client.last_name || '?')[0]}
                        </div>
                        <div>
                          <div className="font-medium text-white">
                            {client.first_name} {client.last_name}
                          </div>
                          <div className="text-xs text-slate-400 sm:hidden">
                            {formatLastContact(client.last_contact_date)}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Risk score */}
                    <td className="px-4 py-3">
                      <RiskScoreBadge score={client.risk_score} />
                    </td>

                    {/* Last contact */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 text-sm text-slate-400">
                        <Clock className="w-3.5 h-3.5" />
                        {formatLastContact(client.last_contact_date)}
                      </div>
                    </td>

                    {/* Action needed */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-sm" style={{ color: category.color }}>
                        {category.action}
                      </span>
                    </td>

                    {/* Quick actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {client.phone && onQuickCall && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onQuickCall(client); }}
                            className="p-2 text-slate-400 hover:text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                            title="Call"
                          >
                            <Phone className="w-4 h-4" />
                          </button>
                        )}
                        {client.phone && onQuickSMS && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onQuickSMS(client); }}
                            className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors"
                            title="SMS"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </button>
                        )}
                        {client.email && onQuickEmail && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onQuickEmail(client); }}
                            className="p-2 text-slate-400 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                            title="Email"
                          >
                            <Mail className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientRiskQueue;
